// Display-name normalization.
//
// Grant 2026-05-16: Schwab/Betterment exports arrive ALL CAPS
// ("THE FERNANDEZ FAMILY TRUST", "LEMAY, REBECCA"), and the OS shows
// them next to title-cased names ("Kavlie Household") — "All caps is
// awful data consistency. Things need to be put in place so they do
// not show up as all caps." This is the shared normalizer that every
// ingestion boundary (AC/financial importers + the future automated
// Charles Schwab and Betterment connectors) and the read/display
// layer route names through, so casing is consistent no matter the
// source.
//
// DESIGN — conservative + idempotent:
//
//   * If the string already contains a lowercase letter we treat it
//     as intentionally cased and DO NOT re-case it (only whitespace /
//     comma cleanup). This guarantees idempotency and means we never
//     mangle a correctly-entered "McDonald Household" or "iRobot LLC".
//   * Only effectively-all-caps strings (no lowercase letters at all)
//     get re-cased to display form.
//   * Re-casing is token-aware: legal/registration acronyms stay
//     UPPER (LLC, IRA, TTEE, UA, JTWROS, Roman numerals…), nobiliary
//     particles + connectors go lower mid-string (von, de, la, and,
//     of, the…), Mc/O'/D'/L' prefixes and hyphenated parts are
//     handled, single-letter initials stay upper, Jr/Sr get a period.
//   * The RAW source string is always preserved by the caller
//     separately (e.g. portfolios.account_title /
//     contacts.*_client_name) — this function is for the DISPLAY
//     value only. Never destroy the original.
//
// This is intentionally NOT a perfect legal-name formatter. Its only
// job is "consistent, human, never-all-caps display casing." Trust /
// account-registration STRUCTURE (a Trust belongs UNDER a household,
// not as one) is a separate schema concern handled by the
// registration-type modeling — not this utility.

// Tokens kept fully UPPERCASE (account registration + legal codes
// that are unambiguous in financial-custodian exports). Deliberately
// excludes ambiguous ones like SEP / CO / NA / PA (collide with month
// "Sep" / words) — those are better handled by registration-type
// modeling, not casing heuristics.
const UPPER_TOKENS = new Set<string>([
  "UA",
  "UAD",
  "UTD",
  "UDT",
  "DTD",
  "FBO",
  "TOD",
  "POD",
  "JT",
  "JTWROS",
  "WROS",
  "TIC",
  "TEN",
  "TTEE",
  "TR",
  // Custodian abbreviations for "trust" (2026-10-14: "DONALD BROOKS TST"
  // was parsed as a person named "Donald Tst").
  "TST",
  "TRST",
  "LLC",
  "LLP",
  "INC",
  "IRA",
  "UTMA",
  "UGMA",
  "HSA",
  "ROTH",
  "SSN",
  "EIN",
  "USA",
  // Roman numerals (generational suffixes) up to X.
  "II",
  "III",
  "IV",
  "VI",
  "VII",
  "VIII",
  "IX",
  // (Intentionally NOT "V" / "X" / "I" / "L" / "C" / "D" / "M" alone —
  //  those collide with single-letter initials / the word "I". Single
  //  letters are handled by the initial rule, which also yields
  //  uppercase, so the common cases still render correctly.)
]);

// Lowercased when NOT the first token: connectors + nobiliary
// particles. "&" is preserved as-is by the tokenizer.
const LOWER_TOKENS = new Set<string>([
  "and",
  "or",
  "of",
  "the",
  "for",
  "a",
  "an",
  "to",
  "in",
  "on",
  "at",
  "by",
  // nobiliary / surname particles
  "von",
  "van",
  "der",
  "den",
  "de",
  "del",
  "della",
  "di",
  "da",
  "dos",
  "das",
  "la",
  "le",
  "du",
  "ter",
  "ten",
  "bin",
  "al",
]);

function titleCaseWord(word: string): string {
  if (word.length === 0) return word;
  return word[0]!.toUpperCase() + word.slice(1).toLowerCase();
}

// Casing for a single hyphen/apostrophe-free word, given its source
// (already known to be all-caps overall) and whether it's first.
function caseToken(token: string, isFirst: boolean): string {
  // Pure punctuation / connector symbols pass through.
  if (token === "&" || /^[^\p{L}\p{N}]+$/u.test(token)) return token;

  const bare = token.replace(/[.,]+$/u, "");
  const trailing = token.slice(bare.length); // preserved punctuation

  const upper = bare.toUpperCase();

  // Suffix Jr / Sr → add the conventional period.
  if (upper === "JR" || upper === "JR.") return "Jr." + trailing;
  if (upper === "SR" || upper === "SR.") return "Sr." + trailing;

  // Registration / legal acronym stays uppercase.
  if (UPPER_TOKENS.has(upper)) return upper + trailing;

  // Single letter = an initial → uppercase (e.g. "Jerry and Susan S").
  if (/^\p{L}$/u.test(bare)) return bare.toUpperCase() + trailing;

  // Pure number (year, "1999") or alphanumeric id → leave as-is.
  if (/\d/u.test(bare)) return bare + trailing;

  // Connector / particle → lowercase, UNLESS it's the first token.
  if (!isFirst && LOWER_TOKENS.has(bare.toLowerCase())) {
    return bare.toLowerCase() + trailing;
  }

  // "Mc" + Name  → "McName"
  if (/^MC[A-Z]{2,}$/u.test(upper)) {
    return "Mc" + titleCaseWord(bare.slice(2)) + trailing;
  }
  // Apostrophe-prefixed: O'BRIEN → O'Brien, D'ANGELO → D'Angelo
  const apos = bare.match(/^([A-Z])(['’])([A-Z].*)$/u);
  if (apos) {
    return (
      apos[1]!.toUpperCase() +
      apos[2]! +
      titleCaseWord(apos[3]!) +
      trailing
    );
  }

  return titleCaseWord(bare) + trailing;
}

function reCaseToken(token: string, isFirst: boolean): string {
  // Handle hyphenated compounds (SMITH-JONES → Smith-Jones) and
  // slash-separated (AND/OR registration noise) by casing each part.
  if (token.includes("-")) {
    return token
      .split("-")
      .map((part, i) => caseToken(part, isFirst && i === 0))
      .join("-");
  }
  return caseToken(token, isFirst);
}

/**
 * Normalize a name for DISPLAY. Idempotent. Returns the input
 * trimmed/space-collapsed if it's already intentionally cased
 * (contains any lowercase letter); re-cases it from all-caps form
 * otherwise. Always tidies whitespace and ", " spacing.
 *
 * @param raw the source/legal string (kept by the caller separately)
 * @returns the display string, or "" for null/blank input
 */
export function normalizeDisplayName(raw: string | null | undefined): string {
  if (raw == null) return "";
  // Whitespace + comma tidy applied regardless of casing decision:
  //   "LEMAY ,  REBECCA" → "LEMAY, REBECCA" → (recased) "Lemay, Rebecca"
  let s = raw
    .replace(/\s+/gu, " ")
    .replace(/\s+,/gu, ",")
    .replace(/,(?=\S)/gu, ", ")
    .trim();
  if (s.length === 0) return "";

  // Already intentionally cased → leave casing, just return tidied.
  if (/\p{Ll}/u.test(s)) return s;

  // Effectively all-caps → re-case token by token.
  const tokens = s.split(" ");
  return tokens
    .map((tok, i) => reCaseToken(tok, i === 0))
    .join(" ")
    .replace(/\s+/gu, " ")
    .trim();
}

/**
 * True when the string looks like a TRUST / legal-entity registration
 * rather than a person/family. Used by the importer + audit to detect
 * accounts that must be modeled as a registration UNDER a household
 * (Grant 2026-05-16: "A Trust MUST be under a household"), not minted
 * as a household of their own. Case-insensitive; works on raw or
 * normalized input.
 */
export function looksLikeTrustOrEntity(raw: string | null | undefined): boolean {
  if (!raw) return false;
  return (
    /\b(trust|tr|tst|trst|ttee|trustee|estate|llc|llp|inc|incorporated|company|foundation|endowment|fbo|ua|uad|utd|udt|dtd|conservatorship|guardianship)\b/iu.test(
      raw,
    ) ||
    ORGANISATION_WORD_RE.test(raw) ||
    /l\.l\.c\.|inc\./iu.test(raw)
  );
}

/**
 * Organisation words that are NOT plausible surnames (2026-09-12).
 *
 * WHY THIS EXISTS. The list above knows "TRUST" and "TTEE" and nothing
 * else, so a registration titled for an organisation without one of those
 * words fell through to the person parser, which takes the first and last
 * token as a given and family name. The ByAllAccounts Schwab export for
 * 2026-09-03 contained 12 such rows and every one of them manufactured a
 * human being:
 *
 *     "UNIVERSITY IMPACT XXXXXX4231"        -> Universit(y) Impact  (×11)
 *     "LANDMARK INSURANCE ASSOCIATES, ..."  -> Landmark Associates,
 *
 * That is the same failure that produced the mess this repair exists for.
 * On 2026-04-25 the whitespace-splitting name parser turned "THE FERNANDEZ
 * FAMILY TRUST" into a person called "The Trust", every other family trust
 * parsed to the same degenerate name, and the matcher filed six unrelated
 * families onto one household. The parser was fixed the following month;
 * the word list it consults was not, so the identical bug was still live on
 * a different shape of name.
 *
 * THE SELECTION RULE, because a false positive here is its own harm: a word
 * belongs on this list only if it is an organisation word AND is not a
 * plausible family name. A surname wrongly graded as an entity stops that
 * person's account attaching to their household — the mirror of the bug
 * being fixed. "Church" is the instructive exclusion: unmistakably an
 * organisation word, and unmistakably somebody's surname, so it stays off.
 * Same reasoning keeps "capital", "group", "services" and "bank" off —
 * each is either a real surname or common enough inside a person's
 * registration to be worth the miss.
 *
 * Deliberately NOT a fix for every shape. A title with no organisation word
 * at all ("SUSAN RANCH HOLDINGS" would be caught; a bare project codename
 * would not) still parses as a person. The honest position is that this
 * closes the shapes in the real data, not the general problem.
 */
const ORGANISATION_WORD_RE =
  /\b(associates?|association|enterprises?|partners|holdings?|corporation|corp|ministry|ministries|university|college|academy|institute|society|council|properties|ventures?|charitable|charities|reit)\b/iu;

export type RegistrationKind =
  | "individual"
  | "joint"
  | "trust"
  | "estate"
  | "ira"
  | "entity"
  | "other";

/**
 * Classify an account's REGISTRATION KIND (Fin-G, Grant 2026-05-16:
 * "A Trust MUST be under a household"). This is a pure, conservative
 * CLASSIFIER — it answers "what kind of registration is this account"
 * so the model can treat a trust as a registration owned by a family
 * household. It does NOT match or attach to any household (that's the
 * separate, analyst-gated grantor-matching step) and never guesses a
 * person's identity.
 *
 * Resolution order:
 *   1. The custodian's own "Registration type" string (Schwab/
 *      Betterment) is the cleanest signal — prefer it when present.
 *   2. Otherwise fall back to scanning the raw holder/title string.
 *   3. Return null when genuinely unsure — a NULL registration_kind
 *      is a valid "not yet classified" state (no forced guess).
 *
 * Order of checks is deliberate: entity (LLC/Inc) and estate are
 * tested before trust because an "ESTATE OF … TRUST" or
 * "… FAMILY LLC TRUST" should classify by the stronger signal;
 * IRA/retirement before individual; joint ("&"/" and " between two
 * party names) before individual.
 */
export function classifyRegistrationKind(
  rawHolderOrTitle: string | null | undefined,
  custodianRegistrationType?: string | null | undefined,
): RegistrationKind | null {
  const probe = (s: string | null | undefined): RegistrationKind | null => {
    if (!s) return null;
    const t = s.toLowerCase();
    if (/\b(llc|l\.l\.c\.|llp|inc\.?|incorporated|corp|company|co\.|foundation|endowment|partnership|lp)\b/u.test(t)) {
      return "entity";
    }
    if (/\bestate\b/u.test(t)) return "estate";
    if (/\b(trust|ttee|trustee|tr|tst|trst|u\/?a|uad|utd|udt|dtd|revocable|irrevocable|living trust)\b/u.test(t)) {
      return "trust";
    }
    if (/\b(ira|roth|sep|simple ira|401\s?k|403\s?b|rollover|beneficiary ira|inherited ira|tsp)\b/u.test(t)) {
      return "ira";
    }
    if (/\b(jt\s?wros|jtwros|jt ten|joint ten|joint tenants?|tenants in common|tic|community property|ten com|jtic)\b/u.test(t)) {
      return "joint";
    }
    if (/\b(individual|single|sole)\b/u.test(t)) return "individual";
    return null;
  };

  // 1. Custodian registration type wins when it classifies cleanly.
  const fromCustodian = probe(custodianRegistrationType);
  if (fromCustodian) return fromCustodian;

  // 2. Fall back to the raw holder / title string.
  const fromName = probe(rawHolderOrTitle);
  if (fromName) return fromName;

  // 2b. A two-party holder ("X & Y" / "X and Y") with no entity
  // markers reads as a joint individual account. NB: `&` is not a
  // word character, so a `\b`-anchored pattern would never match it —
  // match on surrounding whitespace / string ends instead.
  if (
    rawHolderOrTitle &&
    !looksLikeTrustOrEntity(rawHolderOrTitle) &&
    /(?:^|\s)(?:&|and)(?:\s|$)/iu.test(rawHolderOrTitle)
  ) {
    return "joint";
  }

  // 2c. A plain person name with no markers → individual.
  if (
    rawHolderOrTitle &&
    rawHolderOrTitle.trim().length > 0 &&
    !looksLikeTrustOrEntity(rawHolderOrTitle)
  ) {
    return "individual";
  }

  // 3. Unsure — leave NULL (valid "not yet classified" state).
  return null;
}
